import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MEASUREMENT_INPUT_TYPES,
  MEASUREMENT_UNITS,
  addFieldSchema,
  updateFieldSchema,
  type MeasurementInputType,
  type MeasurementUnit,
  type ResolvedMeasurementField,
} from '@tailoring-crm/shared';
import { isApiRequestError } from '../../lib/api/errors.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  Label,
} from '@tailoring-crm/ui';

/**
 * Adding or editing one measurement field.
 *
 * The design decision worth seeing is what this form does NOT offer: any way to change the
 * field's key. When editing, the key is shown as read-only text with a sentence saying why it
 * cannot change. That is the task's "make it impossible to get wrong in the UI rather than
 * warning about it" — a shop renaming "Bust" to "Chest round" is never presented with a
 * control that could orphan three years of measurements, so no warning is needed.
 */

interface FormValues {
  label: string;
  unit: string;
  inputType: MeasurementInputType;
  options: string;
  group: string;
  required: boolean;
  helperText: string;
  active: boolean;
}

const EMPTY: FormValues = {
  label: '',
  unit: '',
  inputType: 'number',
  options: '',
  group: '',
  required: false,
  helperText: '',
  active: true,
};

export interface FieldFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present when editing; absent when adding. */
  field?: ResolvedMeasurementField | null;
  /** The unit a field with no unit of its own will be measured in. */
  inheritedUnit: MeasurementUnit;
  /** Existing section headings, offered so a shop reuses one rather than retyping it. */
  groups: readonly string[];
  onSubmit: (payload: Record<string, unknown>) => Promise<unknown>;
}

export function FieldFormDialog({
  open,
  onOpenChange,
  field = null,
  inheritedUnit,
  groups,
  onSubmit,
}: FieldFormDialogProps): ReactElement {
  const { t } = useTranslation();
  const fieldId = useId();
  const isEdit = field !== null;

  const [values, setValues] = useState<FormValues>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setValues(
      field
        ? {
            label: field.label,
            unit: field.unit ?? '',
            inputType: field.inputType,
            options: field.options.join('\n'),
            group: field.group ?? '',
            required: field.required,
            helperText: field.helperText ?? '',
            active: field.active,
          }
        : EMPTY,
    );
    setErrors({});
    setFormError(null);
  }, [open, field]);

  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]): void => {
    setValues((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => {
      if (!(key in previous)) return previous;
      const next = { ...previous };
      delete next[key as string];
      return next;
    });
  };

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setErrors({});
    setFormError(null);

    const payload: Record<string, unknown> = {
      label: values.label,
      unit: values.unit === '' ? null : values.unit,
      inputType: values.inputType,
      options:
        values.inputType === 'select'
          ? values.options
              .split('\n')
              .map((option) => option.trim())
              .filter(Boolean)
          : [],
      group: values.group.trim() === '' ? null : values.group,
      required: values.required,
      helperText: values.helperText.trim() === '' ? null : values.helperText,
      ...(isEdit ? { active: values.active } : {}),
    };

    const parsed = (isEdit ? updateFieldSchema : addFieldSchema).safeParse(payload);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[issue.path.join('.') || '_'] ??= issue.message;
      setErrors(next);
      return;
    }

    setPending(true);
    try {
      await onSubmit(parsed.data as Record<string, unknown>);
      onOpenChange(false);
    } catch (error) {
      setFormError(
        isApiRequestError(error) || error instanceof Error ? error.message : t('error.unexpected'),
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('measurements.editFieldTitle') : t('measurements.newFieldTitle')}
          </DialogTitle>
          <DialogDescription>{t('measurements.fieldLabelHint')}</DialogDescription>
        </DialogHeader>

        <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)} noValidate>
          {formError ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
            >
              {formError}
            </p>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-label`}>{t('measurements.fieldLabel')}</Label>
            <Input
              id={`${fieldId}-label`}
              value={values.label}
              onChange={(event) => set('label', event.target.value)}
              placeholder={t('measurements.fieldLabelPlaceholder')}
              aria-invalid={errors.label ? true : undefined}
            />
            {errors.label ? (
              <p role="alert" className="text-sm font-medium text-destructive">
                {errors.label}
              </p>
            ) : null}
            {/* Editing: the key, stated plainly and not editable, with the reason why. */}
            {isEdit ? (
              <p className="text-sm text-muted-foreground">
                <code className="rounded bg-muted px-1 py-0.5 text-xs">
                  {t('measurements.keyNote', { key: field.key })}
                </code>{' '}
                {t('measurements.keyExplainer')}
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-unit`}>{t('measurements.fieldUnit')}</Label>
              <select
                id={`${fieldId}-unit`}
                value={values.unit}
                onChange={(event) => set('unit', event.target.value)}
                className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
              >
                <option value="">
                  {t('measurements.unit.inherit')} ({t(`measurements.unit.${inheritedUnit}Short`)})
                </option>
                {MEASUREMENT_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {t(`measurements.unit.${unit}`)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-inputType`}>{t('measurements.fieldInputType')}</Label>
              <select
                id={`${fieldId}-inputType`}
                value={values.inputType}
                onChange={(event) => set('inputType', event.target.value as MeasurementInputType)}
                className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
              >
                {MEASUREMENT_INPUT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`measurements.inputType_${type}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {values.inputType === 'select' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-options`}>{t('measurements.fieldOptions')}</Label>
              <Textarea
                id={`${fieldId}-options`}
                value={values.options}
                onChange={(event) => set('options', event.target.value)}
              />
              <p className="text-sm text-muted-foreground">{t('measurements.fieldOptionsHint')}</p>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-group`}>{t('measurements.fieldGroup')}</Label>
            <Input
              id={`${fieldId}-group`}
              value={values.group}
              list={`${fieldId}-group-options`}
              onChange={(event) => set('group', event.target.value)}
              placeholder={t('measurements.fieldGroupPlaceholder')}
            />
            <datalist id={`${fieldId}-group-options`}>
              {groups.map((group) => (
                <option key={group} value={group} />
              ))}
            </datalist>
            <p className="text-sm text-muted-foreground">{t('measurements.fieldGroupHint')}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-helperText`}>{t('measurements.fieldHelperText')}</Label>
            <Input
              id={`${fieldId}-helperText`}
              value={values.helperText}
              onChange={(event) => set('helperText', event.target.value)}
            />
            <p className="text-sm text-muted-foreground">{t('measurements.fieldHelperTextHint')}</p>
          </div>

          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              className="size-4"
              checked={values.required}
              onChange={(event) => set('required', event.target.checked)}
            />
            {t('measurements.fieldRequired')}
          </label>

          {/* Deactivation lives here rather than behind a Delete button, because for a field
              that has ever been measured it is the ONLY thing that can happen to it. */}
          {isEdit ? (
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  className="size-4"
                  checked={values.active}
                  onChange={(event) => set('active', event.target.checked)}
                />
                {t('measurements.fieldActive')}
              </label>
              <p className="text-sm text-muted-foreground">{t('measurements.fieldActiveHint')}</p>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
